import type {
  KeywordAlternateAction,
  KeywordRecommendationItem,
  SearchTermRecommendationItem,
  SpKeywordApplyAction,
} from '@/types/keywordRecommendations';
import { keywordRowKey, searchTermRowKey } from '@/api/recommendationFormat';

export function buildApplyAction(
  row: KeywordRecommendationItem,
  bidOverrides: Record<string, number>
): SpKeywordApplyAction | null {
  const action = row.recommendation.action;
  const key = keywordRowKey(row.keywordId);
  if (action === 'INCREASE_BID' || action === 'DECREASE_BID') {
    const bid = bidOverrides[key] ?? row.recommendation.recommendedBid;
    if (bid == null) return null;
    return { action, keywordId: row.keywordId, bid };
  }
  if (action === 'PAUSE') {
    return { action, keywordId: row.keywordId };
  }
  return null;
}

export function buildSearchTermApplyAction(
  row: SearchTermRecommendationItem,
  bidOverrides: Record<string, number>
): SpKeywordApplyAction | null {
  const action = row.recommendation.action;
  const key = searchTermRowKey(row.campaignId, row.adGroupId, row.searchTerm);
  if (action === 'ADD_KEYWORD') {
    const bid = bidOverrides[key] ?? row.recommendation.suggestedBid ?? 1.0;
    return {
      action,
      campaignId: row.campaignId,
      adGroupId: row.adGroupId,
      keywordText: row.searchTerm,
      matchType: row.recommendation.matchType || 'EXACT',
      bid,
    };
  }
  if (action === 'ADD_NEGATIVE_KEYWORD') {
    return {
      action,
      campaignId: row.campaignId,
      adGroupId: row.adGroupId,
      keywordText: row.searchTerm,
      matchType: row.recommendation.matchType || 'NEGATIVE_EXACT',
    };
  }
  return null;
}

export function getKeywordNegativeAlternate(
  row: KeywordRecommendationItem
): KeywordAlternateAction | undefined {
  return row.alternateActions?.find((a) => a.action === 'ADD_NEGATIVE_KEYWORD');
}

export function buildKeywordNegativeApplyAction(
  alternate: KeywordAlternateAction,
  override?: { campaignId: string; adGroupId: string }
): SpKeywordApplyAction {
  return {
    action: 'ADD_NEGATIVE_KEYWORD',
    campaignId: override?.campaignId ?? alternate.campaignId,
    adGroupId: override?.adGroupId ?? alternate.adGroupId,
    keywordText: alternate.keywordText,
    matchType: alternate.matchType,
  };
}

export function buildKeywordPauseAction(row: KeywordRecommendationItem): SpKeywordApplyAction {
  return { action: 'PAUSE', keywordId: row.keywordId };
}
